/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  getPreviousSearches
 */

import {
  type AutocompleteApiResult,
  AutocompleteResult,
  type RawAutocompleteApiResult,
} from '@zcode/bizos'
import { localStorageSingleton } from '@zcode/nr-utils'
import { toSlug } from '@zcode/utils'
import invariant from 'tiny-invariant'
import type * as Api from '../../../../shared/api'

type LegacyRawSavedAutocompleteResult = { isPreviousSearch: true } & Pick<
  AutocompleteResult,
  | 'box'
  | 'city'
  | 'id'
  | 'type'
  | 'name'
  | 'state'
  | 'country'
  | 'lat'
  | 'lng'
  | 'url'
  | 'zoom'
  | 'hood'
>

type NewRawSavedAutocompleteResult = RawAutocompleteApiResult & {
  readonly is_previous_search: true
}

export type RawSavedAutocompleteResult =
  | LegacyRawSavedAutocompleteResult
  | NewRawSavedAutocompleteResult

export const getRawPreviousSearches = () => {
  const previousSearches: RawSavedAutocompleteResult[] | undefined =
    localStorageSingleton.get('prevSearches')
  return previousSearches
    ?.filter((search) => 'record_type' in search || 'type' in search)
    ?.map((search): NewRawSavedAutocompleteResult => {
      // @ts-expect-error
      let newSearch: NewRawSavedAutocompleteResult =
        'record_id' in search
          ? search
          : {
              box: search.box
                ? [search.box.minLng, search.box.minLat, search.box.maxLng, search.box.maxLat]
                : undefined,
              city: search.city,
              country: search.country,
              is_previous_search: true,
              lat: search.lat,
              lng: search.lng,
              name: search.name,
              record_id: search.id,
              record_type: search.type,
              state: search.state,
              url: search.url,
            }
      if (newSearch.record_type === 'postcode' && !(newSearch as RawAutocompleteApiResult).url) {
        // the v1 api returned postcodes without url, so we'll need to add that ourselves
        newSearch = {
          ...newSearch,
          // @ts-expect-error string isn't assignable to `${string}-${string}/${number}`
          url: toSlug(newSearch.city, newSearch.state, newSearch.name),
        }
      }
      return newSearch
    })
}

export const getPreviousSearches = (
  exclude: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude']
) =>
  getRawPreviousSearches()
    ?.map((raw): AutocompleteApiResult => {
      const result = new AutocompleteResult(raw)
      invariant(!result.isDerivedFromEntity && result.type !== 'current_position')
      return result
    })
    .filter((result) => !exclude?.includes(result.type))
